import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { useUnit } from '@/contexts/UnitContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { DashboardPaymentLinksSection } from '@/components/payment-links/DashboardPaymentLinksSection';
import { Event, UnitMembership, UnitType } from '@/types';
import { formatDate, formatCurrency } from '@/lib/utils';
import { unitTypeRequiresNumber } from '@/lib/unit-number-display';
import { normalizeCreditsSystemModeFromPlatformData } from '@/lib/creditsPlatformMode';
import type { CreditPackRow } from '@masonic-tools/shared-types';
import { DEFAULT_CREDIT_PACK_ROWS } from '@masonic-tools/shared-types';
import { Calendar, Plus, Users, Settings, ArrowLeft, Shield, Wallet } from 'lucide-react';
import { showSuccess, showError } from '@/lib/swal';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function UnitDashboardPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { units, currentUnit, setCurrentUnit, fetchUnitById } = useUnit();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [entityName, setEntityName] = useState<string>('Unit');
  // Whether this unit's type uses a unit number (defaults to true for legacy units)
  const [showUnitNumber, setShowUnitNumber] = useState<boolean>(true);
  const [isAdminViewing, setIsAdminViewing] = useState(false);
  const navigate = useNavigate();
  const isAdmin = userProfile?.isAdmin === true;
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [creditTxs, setCreditTxs] = useState<Array<Record<string, unknown> & { id: string }>>([]);
  const [creditPacks, setCreditPacks] = useState<CreditPackRow[]>(DEFAULT_CREDIT_PACK_ROWS);
  const [selectedPackId, setSelectedPackId] = useState<string>(DEFAULT_CREDIT_PACK_ROWS[0]?.id ?? '');
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [creditsSystemMode, setCreditsSystemMode] = useState<'enabled' | 'disabled' | 'admin_only'>('enabled');

  useEffect(() => {
    const cp = searchParams.get('creditPurchase');
    if (cp === 'success') {
      showSuccess('Credit purchase completed.');
      setSearchParams(
        (p) => {
          const n = new URLSearchParams(p);
          n.delete('creditPurchase');
          return n;
        },
        { replace: true },
      );
    } else if (cp === 'cancelled') {
      showError('Credit purchase was cancelled.');
      setSearchParams(
        (p) => {
          const n = new URLSearchParams(p);
          n.delete('creditPurchase');
          return n;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!unitId) return;
    const ref = doc(db, 'units', unitId);
    const unsub = onSnapshot(ref, (snap) => {
      const v = snap.data()?.creditBalance;
      setCreditBalance(typeof v === 'number' ? v : 0);
    });
    return () => unsub();
  }, [unitId]);

  useEffect(() => {
    if (!unitId) return;
    const q = query(
      collection(db, 'units', unitId, 'creditTransactions'),
      orderBy('createdAt', 'desc'),
      limit(40),
    );
    const unsub = onSnapshot(q, (snap) => {
      setCreditTxs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [unitId]);

  useEffect(() => {
    const loadPacks = async () => {
      try {
        const s = await getDoc(doc(db, 'systemSettings', 'platform'));
        const raw = s.data()?.creditPacks;
        const mode = normalizeCreditsSystemModeFromPlatformData(s.data() as Record<string, unknown> | undefined);
        setCreditsSystemMode(mode);
        if (Array.isArray(raw) && raw.length > 0) {
          const cleaned = raw.filter(
            (p: { id?: string; credits?: unknown; pricePence?: unknown }) =>
              p && typeof p.id === 'string' && typeof p.credits === 'number' && typeof p.pricePence === 'number',
          ) as CreditPackRow[];
          if (cleaned.length > 0) {
            setCreditPacks(cleaned);
            setSelectedPackId(cleaned[0].id);
            return;
          }
        }
        setCreditPacks([...DEFAULT_CREDIT_PACK_ROWS]);
        setSelectedPackId(DEFAULT_CREDIT_PACK_ROWS[0].id);
      } catch {
        setCreditPacks([...DEFAULT_CREDIT_PACK_ROWS]);
        setSelectedPackId(DEFAULT_CREDIT_PACK_ROWS[0].id);
      }
    };
    loadPacks();
  }, []);

  useEffect(() => {
    const loadUnit = async () => {
      if (!unitId) {
        setLoading(false);
        return;
      }

      // First, check if unit is in the user's units
      if (units.length > 0) {
        const unit = units.find((u) => u.id === unitId);
        if (unit && (!currentUnit || currentUnit.id !== unitId)) {
          setCurrentUnit(unit);
          setIsAdminViewing(false); // User is a member, not admin viewing
          setLoading(false);
          return;
        }
      }

      // If not found and user is admin, try to fetch directly
      if (isAdmin && (!currentUnit || currentUnit.id !== unitId)) {
        setLoading(true);
        const unit = await fetchUnitById(unitId);
        if (unit) {
          setCurrentUnit(unit);
          setIsAdminViewing(true); // Admin viewing unit they're not a member of
        }
        setLoading(false);
        return;
      }

      // If not admin and unit not found, they don't have access
      if (!isAdmin) {
        setIsAdminViewing(false);
      }

      setLoading(false);
    };

    loadUnit();
  }, [unitId, units, currentUnit, setCurrentUnit, fetchUnitById, isAdmin]);

  // Fetch unit type to get entity name
  useEffect(() => {
    const fetchUnitType = async () => {
      if (!currentUnit) {
        setEntityName('Unit');
        return;
      }

      // Check if unit has entityName directly
      if ((currentUnit as any).entityName) {
        setEntityName((currentUnit as any).entityName);
        return;
      }

      // If no typeId, default to 'Unit' for legacy units
      if (!currentUnit?.typeId) {
        console.log('No typeId found for unit, defaulting to Unit');
        setEntityName('Unit');
        return;
      }

      try {
        console.log('Fetching unit type for typeId:', currentUnit.typeId);
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          const unitTypes: UnitType[] = data.unitTypes || [];
          console.log('Available unit types:', unitTypes);
          const unitType = unitTypes.find((ut) => ut.id === currentUnit.typeId);
          if (unitType) {
            console.log('Found unit type:', unitType);
            setEntityName(unitType.entityName);
            setShowUnitNumber(unitTypeRequiresNumber(unitType));
          } else {
            console.log('Unit type not found, defaulting to Unit');
            setEntityName('Unit');
          }
        } else {
          console.log('No system settings found, defaulting to Unit');
          setEntityName('Unit');
        }
      } catch (error) {
        console.error('Error fetching unit type:', error);
        setEntityName('Unit');
      }
    };

    fetchUnitType();
  }, [currentUnit]);

  useEffect(() => {
    if (!unitId) {
      setLoading(false);
      return;
    }

    const eventsQuery = query(
      collection(db, 'units', unitId, 'events'),
      where('eventStatus', 'in', [
        'published',
        'draft',
        'interest-only',
        'closed',
        'completed',
      ]),
      orderBy('date', 'asc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(
      eventsQuery,
      (eventsSnapshot) => {
        const fetchedEvents: Event[] = eventsSnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const eventDate = data.date?.toDate() || new Date();
          return {
            id: docSnap.id,
            unitId: unitId,
            name: data.name,
            date: eventDate,
            location: data.location,
            costPerPerson: data.costPerPerson,
            questionnaire: data.questionnaire || [],
            maxCapacity: data.maxCapacity,
            currentAttendees: data.currentAttendees || 0,
            waitlistEnabled: data.waitlistEnabled || false,
            waitlist: data.waitlist || [],
            rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
            paymentDeadline: data.paymentDeadline?.toDate() || eventDate,
            eventStatus: data.eventStatus,
            dresscode: data.dresscode,
            customInstructions: data.customInstructions,
            isPublic: data.isPublic ?? false,
            requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
            createdBy: data.createdBy,
            createdAt: data.createdAt?.toDate() || new Date(),
          };
        });

        setEvents(fetchedEvents);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching events:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [unitId]);

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading unit dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUnit) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center px-4">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Unit Not Found</CardTitle>
              <CardDescription>
                The unit you're looking for doesn't exist or you don't have access to it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const userRole = userProfile?.units?.find((u: UnitMembership) => u.unitId === currentUnit.id)?.role;
  const canBuyCredits = userRole === 'Secretary' || userRole === 'Treasurer';
  const isUnitAdmin = userRole === 'Steward' || userRole === 'Secretary' || userRole === 'Treasurer';
  // Platform admin can view any unit, but only edit if they're also a unit admin
  const canManageUnit = isAdmin || isUnitAdmin;

  const canSeeCredits = creditsSystemMode === 'enabled' || (creditsSystemMode === 'admin_only' && isAdmin);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header />
      <main className="flex w-full min-w-0 flex-1 overflow-x-hidden py-6 sm:py-8">
        <DashboardContentContainer>
          {isAdminViewing && (
            <Alert variant="warning" className="mb-6">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <AlertDescription>
                  <strong>Admin Access:</strong> You are viewing this {entityName.toLowerCase()} dashboard using administrator privileges. You are not a member of this {entityName.toLowerCase()}, so some actions may be restricted based on unit-specific permissions.
                </AlertDescription>
              </div>
            </Alert>
          )}
          <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div className="min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="mb-2 h-auto max-w-full justify-start whitespace-normal px-3 text-left text-sm sm:text-base"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="mr-2 h-4 w-4 shrink-0" />
                Back to Personal Dashboard
              </Button>
              <h1 className="break-words text-2xl font-bold tracking-tight sm:text-3xl">
                {currentUnit.name}
              </h1>
              <p 
                className="mt-1 flex cursor-pointer flex-wrap items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground sm:text-base"
                onClick={() => navigate(`/unit/${currentUnit.id}/settings`)}
                title="Click to manage settings"
              >
                {entityName}
                {showUnitNumber && (currentUnit.unitNumber || currentUnit.lodgeNumber) && ` #${currentUnit.unitNumber || currentUnit.lodgeNumber}`}
                <Settings className="h-3 w-3 shrink-0" />
              </p>
            </div>
            {canManageUnit && (
              <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto">
                {canSeeCredits && (userRole === 'Secretary' || userRole === 'Treasurer' || isAdmin) && (
                  <Button 
                    variant="outline" 
                    className="w-full shrink-0 sm:w-auto"
                    onClick={() => navigate(`/unit/${currentUnit.id}/credits`)}
                  >
                    <Wallet className="mr-2 h-4 w-4" />
                    <span className={creditBalance !== null && creditBalance < 0 ? 'text-destructive font-semibold' : ''}>
                      {creditBalance === null ? '…' : creditBalance} Credits
                    </span>
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="w-full shrink-0 sm:w-auto"
                  onClick={() => navigate(`/unit/${currentUnit.id}/settings`)}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  {entityName} Settings
                </Button>
              </div>
            )}
          </div>

          {/* Unit Overview */}
          <div className="mb-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <Card className="min-w-0">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium leading-tight sm:text-sm">Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl font-bold tabular-nums sm:text-2xl">{currentUnit.members.length}</div>
                <p className="text-[11px] text-muted-foreground sm:text-xs">
                  Unit members
                </p>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium leading-tight sm:text-sm">Upcoming Events</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl font-bold tabular-nums sm:text-2xl">
                  {events.filter((e) => e.eventStatus === 'published' && e.date > new Date()).length}
                </div>
                <p className="text-[11px] text-muted-foreground sm:text-xs">Published events</p>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium leading-tight sm:text-sm">Interest Only</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl font-bold tabular-nums sm:text-2xl">
                  {events.filter((e) => e.eventStatus === 'interest-only').length}
                </div>
                <p className="text-[11px] text-muted-foreground sm:text-xs">Collecting interest</p>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium leading-tight sm:text-sm">Draft Events</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl font-bold tabular-nums sm:text-2xl">
                  {events.filter((e) => e.eventStatus === 'draft').length}
                </div>
                <p className="text-[11px] text-muted-foreground sm:text-xs">Ready to publish</p>
              </CardContent>
            </Card>

            <Card className="min-w-0 sm:col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium leading-tight sm:text-sm">Total Attendees</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xl font-bold tabular-nums sm:text-2xl">
                  {events.reduce((sum, e) => sum + e.currentAttendees, 0)}
                </div>
                <p className="text-[11px] text-muted-foreground sm:text-xs">Across all events</p>
              </CardContent>
            </Card>
          </div>



          {/* Events List */}
          <div>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold sm:text-2xl">Events</h2>
              {canManageUnit && (
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => navigate(`/unit/${currentUnit.id}/event/create`)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Event
                </Button>
              )}
            </div>
            {events.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Calendar className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="text-muted-foreground">No events yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
                {events.map((event) => (
                  <Card
                    key={event.id}
                    className="min-w-0 cursor-pointer transition-shadow hover:shadow-lg"
                    onClick={() => navigate(`/unit/${currentUnit.id}/event/${event.id}`)}
                  >
                    <CardHeader className="space-y-2 pb-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                        <CardTitle className="min-w-0 break-words text-base leading-snug sm:text-lg">
                          {event.name}
                        </CardTitle>
                        <Badge
                          className="w-fit shrink-0 self-start"
                          variant={
                            event.eventStatus === 'published'
                              ? 'success'
                              : event.eventStatus === 'interest-only'
                                ? 'secondary'
                                : event.eventStatus === 'draft'
                                  ? 'warning'
                                  : event.eventStatus === 'closed'
                                    ? 'outline'
                                    : event.eventStatus === 'completed'
                                      ? 'secondary'
                                      : 'outline'
                          }
                        >
                          {event.eventStatus}
                        </Badge>
                      </div>
                      <CardDescription className="text-xs sm:text-sm">{formatDate(event.date)}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2 text-xs text-muted-foreground sm:text-sm">
                        <div className="flex min-w-0 items-start gap-2">
                          <Calendar className="mt-0.5 h-4 w-4 shrink-0" />
                          <span className="min-w-0 break-words">{event.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 shrink-0" />
                          <span className="tabular-nums">
                            {event.currentAttendees} attendees
                            {event.maxCapacity && ` / ${event.maxCapacity}`}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {canManageUnit && <DashboardPaymentLinksSection unitId={currentUnit.id} />}
          </div>
        </DashboardContentContainer>
      </main>
    </div>
  );
}


